import { TWIN_TYPE } from './config.js';
import { zygosityComponents } from './twinLinkage.js';
import { orderSiblings } from './helpers.js';

// TGCLOUD-1789 — ONE definition of "may this row be drawn as it stands?", shared by the
// layout engine's order projection and by the drag helper.
//
// The two used to disagree, and the disagreement was settled after the drag had already
// been saved. `_atomOrder` (TGCLOUD-1692) required a twin set to be ROW-contiguous —
// nobody at all between its members — and re-sorted the members internally by birth
// order, discarding any stored rank. So a user who dragged a spouse between two co-twins
// had the drop accepted, locked, checkpointed and POSTed, and then silently rewritten by
// the next layout.
//
// The renderer never needed that much. `twinGroupPath` judges glyph adjacency over the
// SIBSHIP (`cohortPids` comes from `Family.getSibshipOf`, which is full siblings only), so
// the only interposer that can cost a BAR is a sibling. A married-in spouse standing
// between co-twins takes nothing away — indeed the unlocked engine seats a married male
// twin's wife there by itself, and the desktop ancestor does the same.
//
// TGCLOUD-1786 found the rest of the cost. A bar is not the only ink at stake: the twins
// hang from ONE apex on their parents' sibship line, and their lines fan out from it to
// each twin. Anyone standing inside that fan whose own parents are drawn has a line of
// descent coming down from above, and it has to cross the fan to reach them. Replaying
// the import's stale order seated the paternal HALF-sibling between the DZ twins (two
// crossings, beacon lit) and a COUSIN between the MZ twins, and 1789's rule admitted both
// because neither is a full sibling. A married-in spouse has no line coming down, so
// nothing is crossed: that is why 1789's D1 still holds for them, and now only for them
// (1786 decision D6).
//
// Hence two clauses, and only two:
//
//   (a) NOTHING CROSSES THE FAN. No one outside the set may stand between two of its
//       members if they hang from a drawn parental line — a full sibling, a half-sibling,
//       a cousin, or an in-law whose own parents are on the chart. Only someone with no
//       drawn parents (a married-in spouse) may. A full sibling stays refused even when
//       its parents are not drawn, because it is still in the sibship `zygosityRuns`
//       reads, and that is what keeps the bar drawable.
//
//   (b) MONOZYGOTIC CONTIGUITY. Within one set, every monozygotic component must be
//       consecutive among that set's members, or the bar asserting "these two are
//       identical" cannot be drawn at all.
//
// Unknown-zygosity components are deliberately NOT a clause. `orderWithinTwinSet`
// documents why: requiring several overlapping subsets each to be consecutive is not
// always satisfiable, and where the two levels conflict monozygotic wins, because a
// missing bar loses a genetic fact where a missing "?" loses only "we did not record it".
// Both remain omissions rather than false claims, which is the property `zygosityRuns`
// exists to guarantee. Repair still improves an unknown run where it can, via
// `orderSiblings`; it is simply never grounds for refusing a drag.

const setIdOf = (p) => (p && p.twinSubGroupId) || null;

/**
 * The sibship a person belongs to, as `Family.buildSiblingGroups` defines it: the same
 * mother AND the same father, with at least one of the two recorded. Returns null for
 * anyone who cannot be in a sibship (a married-in spouse with no parents recorded).
 */
const sibshipKeyOf = (p) => {
  if (!p) return null;
  const hasParent = p.motherId != null || p.fatherId != null;
  if (!hasParent) return null;
  return `${p.motherId ?? ''}|${p.fatherId ?? ''}`;
};

/**
 * The parents of `p` that are actually drawn: a `fatherId` / `motherId` naming someone
 * `personOf` can find. A parent who is recorded but not on the chart — hidden, which takes
 * them out of the layout input altogether, or a stale reference — draws no line, so it
 * cannot cross anything.
 *
 * Without a `personOf` every recorded parent is taken as drawn. That is the cautious
 * reading: wrongly admitting an interposer costs a crossing on the chart, wrongly refusing
 * one costs a single drag. Both real callers pass one.
 */
const drawnParentsOf = (p, personOf) =>
  [p.fatherId, p.motherId].filter(
    (id) => id != null && (!personOf || personOf(id) != null)
  );

const samePid = (a, b) => a != null && b != null && String(a) === String(b);

/** Partners by either side's spouseIds. */
const partnered = (a, b) =>
  !!a &&
  !!b &&
  ((a.spouseIds || []).some((sid) => samePid(sid, b.pid)) ||
    (b.spouseIds || []).some((sid) => samePid(sid, a.pid)));

/**
 * What an interposer is to the set — for the refusal message only. The rule treats every
 * interposer alike, because every one of them has a line that crosses the fan.
 *
 * 'sibling' also covers a same-parents sibling whose parent slots are recorded the other
 * way round (TGCLOUD-1786 §5: add-child on an inverted couple writes the correct
 * orientation), which the sibship key misses but the user still knows as a brother or
 * sister.
 */
const relationOf = (between, members, personOf) => {
  const couple = (p) =>
    [p.fatherId, p.motherId]
      .filter((id) => id != null)
      .map(String)
      .sort()
      .join('|');
  const theirCouple = couple(between);
  if (theirCouple && members.some((m) => couple(m) === theirCouple))
    return 'sibling';

  const setParents = members.flatMap((m) => [m.fatherId, m.motherId]);
  const theirParents = drawnParentsOf(between, personOf);
  if (theirParents.some((id) => setParents.some((q) => samePid(id, q))))
    return 'half-sibling';

  if (members.some((m) => partnered(m, between))) return 'partner';

  if (personOf) {
    // A cousin: one of their parents shares a parent with one of the set's parents.
    const parentsOfParents = (ids) =>
      ids
        .map((id) => (id == null ? null : personOf(id)))
        .filter(Boolean)
        .flatMap((parent) => [parent.fatherId, parent.motherId])
        .filter((id) => id != null);
    const ours = parentsOfParents(setParents);
    if (
      parentsOfParents(theirParents).some((id) =>
        ours.some((q) => samePid(id, q))
      )
    )
      return 'cousin';
  }
  return 'relative';
};

/** The twin sets present in `row`, each as its members with their row indices. */
const setsIn = (row) => {
  const sets = new Map();
  row.forEach((p, i) => {
    const id = setIdOf(p);
    if (!id) return;
    if (!sets.has(id)) sets.set(id, []);
    sets.get(id).push({ person: p, index: i });
  });
  for (const [id, entries] of [...sets])
    if (entries.length < 2) sets.delete(id);
  return sets;
};

/**
 * Clause (a) for one set: everyone standing inside the set's span who may not stand
 * there, each as `{ index, person, relation }`. Shared by the check and the repair, so
 * the two cannot disagree about who is an interposer.
 */
const interposersOf = (people, entries, personOf) => {
  const members = entries.map((e) => e.person);
  const memberPids = new Set(members.map((m) => String(m.pid)));
  const sibshipKeys = new Set(
    members.map((m) => sibshipKeyOf(m)).filter(Boolean)
  );
  const first = entries[0].index;
  const last = entries[entries.length - 1].index;
  const out = [];
  for (let i = first + 1; i < last; i += 1) {
    const between = people[i];
    if (!between || memberPids.has(String(between.pid))) continue;
    const key = sibshipKeyOf(between);
    const fullSibling = !!key && sibshipKeys.has(key);
    if (!fullSibling && !drawnParentsOf(between, personOf).length) continue;
    out.push({
      index: i,
      person: between,
      relation: fullSibling ? 'sibling' : relationOf(between, members, personOf)
    });
  }
  return out;
};

/**
 * Every reason `row` (persons or render nodes, left to right) cannot be drawn honestly.
 *
 * Each violation is `{ kind, setId, pid }`. For clause (a) `kind` is `'sibling-between'`,
 * `pid` is the interposer, and `relation` says what they are to the set (`'sibling'`,
 * `'half-sibling'`, `'partner'`, `'cousin'` or `'relative'`), which only the message
 * reads. For clause (b) `kind` is `'identical-split'` and `pid` is the first member of the
 * component that ended up separated.
 *
 * `personOf(pid)` resolves a pid to the person or node drawn for it, or null when nobody
 * is drawn for it. It is how clause (a) tells a drawn parental line from a merely
 * recorded one; see `drawnParentsOf` for what happens without it.
 *
 * Nodes that carry no `twinRelations` simply yield no clause (b) findings: with no
 * zygosity data there is nothing to assert, and refusing on absent data would block
 * drags on any surface that does not publish the field.
 */
export function twinOrderViolations(row, personOf = null) {
  const people = (row || []).filter(Boolean);
  const out = [];

  for (const [setId, entries] of setsIn(people)) {
    const members = entries.map((e) => e.person);

    // (a) anyone hanging from a drawn parental line, standing inside the set's span.
    for (const { person, relation } of interposersOf(
      people,
      entries,
      personOf
    )) {
      out.push({ kind: 'sibling-between', setId, pid: person.pid, relation });
    }

    // (b) a monozygotic component split across the set's own members.
    if (!members.some((m) => Array.isArray(m.twinRelations))) continue;
    const seatOf = new Map(members.map((m, i) => [String(m.pid), i]));
    for (const component of zygosityComponents(
      members,
      TWIN_TYPE.monozygotic
    )) {
      const seats = component
        .map((pid) => seatOf.get(String(pid)))
        .filter((s) => s != null)
        .sort((a, b) => a - b);
      if (seats.length < 2) continue;
      if (seats[seats.length - 1] - seats[0] !== seats.length - 1) {
        out.push({ kind: 'identical-split', setId, pid: component[0] });
      }
    }
  }

  return out;
}

/** True when nothing about the twin sets in `row` stops the chart telling the truth. */
export const isDrawableTwinOrder = (row, personOf = null) =>
  twinOrderViolations(row, personOf).length === 0;

/**
 * A sentence for the user, naming what the refused drop would have cost. Kept here beside
 * the rule so the wording cannot drift from the thing being enforced (TGCLOUD-1789 AC2:
 * a refusal has to be visible, not a silent snap-back).
 *
 * Every clause (a) refusal but a full sibling's has the same cause, a line of descent
 * coming down through the twins' lines, so the sentence names who and then says that. A
 * partner is also told who MAY stand there, because that is the drop they were most
 * likely reaching for.
 */
export function describeTwinViolation(violation) {
  if (!violation) return null;
  if (violation.kind === 'identical-split') {
    return 'Identical twins have to stay side by side, or the line recording that they are identical cannot be drawn.';
  }
  const crosses =
    'their line of descent would cross the lines joining the twins.';
  // The actual condition, not "a partner who married into the family": a cross-married
  // partner married into the family too, and is exactly who the partner refusal refuses.
  const mayStand = 'A partner whose parents are not on the chart can be.';
  switch (violation.relation) {
    case 'half-sibling':
      return `A half-brother or half-sister cannot be placed between twins: ${crosses}`;
    case 'cousin':
      return `A cousin cannot be placed between twins: ${crosses}`;
    case 'partner':
      return `A partner whose own parents are on the chart cannot be placed between twins: ${crosses} ${mayStand}`;
    case 'relative':
      return `Someone whose parents are on the chart cannot be placed between twins: ${crosses}`;
    default:
      return `A brother or sister cannot be placed between twins. ${mayStand}`;
  }
}

/**
 * The smallest change to `row` that makes it drawable, or `row` itself when it already is.
 *
 * This is what the engine applies to a stored order, in place of the old atom rule. A
 * stored order that is already drawable is returned untouched, which is the whole point:
 * the arrangement a user built is honoured, and only a genuine conflict is corrected.
 *
 * Clause (b) is repaired first, by re-seating one set's members among the slots they
 * already occupy, so it cannot disturb anything outside the set. Clause (a) is repaired
 * second, by moving each interposer to the nearer edge of the set's span — interposers
 * from another sibship together, as one block.
 *
 * `personOf` is as for `twinOrderViolations`; the engine passes its own person lookup.
 */
export function repairTwinOrder(row, personOf = null) {
  let people = (row || []).filter(Boolean);
  if (people.length < 3) return people;

  // (b) re-seat a set whose monozygotic component is split. `orderSiblings` over the
  // set's own members is the same routine the unlocked engine uses, so a repaired set
  // comes out in the arrangement the chart would have chosen for it anyway.
  for (const [setId] of setsIn(people)) {
    const split = twinOrderViolations(people, personOf).some(
      (v) => v.kind === 'identical-split' && v.setId === setId
    );
    if (!split) continue;
    const seats = [];
    const members = [];
    people.forEach((p, i) => {
      if (setIdOf(p) !== setId) return;
      seats.push(i);
      members.push(p);
    });
    const reseated = orderSiblings([...members]);
    const next = [...people];
    seats.forEach((seat, i) => {
      next[seat] = reseated[i];
    });
    people = next;
  }

  // (a) move each interposer out to the nearer edge of the span it is breaking.
  // Recomputed per set because every move renumbers the row.
  for (const setId of [...setsIn(people).keys()]) {
    const entries = setsIn(people).get(setId);
    if (!entries) continue;
    const first = entries[0].index;
    const last = entries[entries.length - 1].index;
    const leftward = [];
    const rightward = [];

    // The set's own brothers and sisters go to their nearer edge one by one: they hang
    // from the same sibship line as the twins, so splitting them either side crosses
    // nothing. Interposers from ANOTHER sibship go as one block, to whichever edge costs
    // them least travel in total. Sent one by one, the two cousins seated inside the MZ
    // pair on the 1786 family (C1534) came out either side of it, and their own sibship
    // line then crossed the twins' drop — the very crossing this clause removes, with the
    // beacon still lit. As a block they leave the chart uncrossed.
    const blocks = new Map();
    for (const found of interposersOf(people, entries, personOf)) {
      const key =
        found.relation === 'sibling'
          ? `own:${found.person.pid}`
          : sibshipKeyOf(found.person);
      if (!blocks.has(key)) blocks.set(key, []);
      blocks.get(key).push(found);
    }
    // A block holding the PARTNER of the member at one edge of the span goes to that
    // edge, whatever the travel costs (TGCLOUD-1786 review). Sent the other way, the
    // co-twin ends up standing between the couple — a blocked marriage bar (D8), with
    // the couple's children reading as the co-twin's — and [4, 5, 3] with 5 married to
    // 3 came out [5, 4, 3] where [4, 3, 5] is clean. A partner of a middle member has
    // no such edge, and a block partnered to both edges no preference: the nearer
    // edge decides, as before.
    const headMember = entries[0].person;
    const tailMember = entries[entries.length - 1].person;
    const partnerOf = (member) => (f) => partnered(f.person, member);
    for (const [key, block] of blocks) {
      const leftCost = block.reduce((s, f) => s + (f.index - first), 0);
      const rightCost = block.reduce((s, f) => s + (last - f.index), 0);
      const byHead = block.some(partnerOf(headMember));
      const byTail = block.some(partnerOf(tailMember));
      let left = leftCost <= rightCost;
      if (byHead !== byTail) {
        left = byHead;
      } else if (leftCost === rightCost && !key.startsWith('own:')) {
        // A dead heat goes to the side where the rest of their sibship already stands.
        const kin = (from, to) =>
          people.slice(from, to).filter((p) => sibshipKeyOf(p) === key).length;
        left = kin(0, first) >= kin(last + 1, people.length);
      }
      (left ? leftward : rightward).push(...block);
    }
    if (!leftward.length && !rightward.length) continue;
    // Whatever moves keeps its own left-to-right order, except that a partner of the
    // edge member it lands beside is seated next to them.
    const inRowOrder = (list) =>
      [...list].sort((a, b) => a.index - b.index).map((f) => f.person);
    const nearest = (list, member, atEnd) => {
      const theirs = list.filter((p) => partnered(p, member));
      const rest = list.filter((p) => !partnered(p, member));
      return atEnd ? [...rest, ...theirs] : [...theirs, ...rest];
    };
    const toLeft = nearest(inRowOrder(leftward), headMember, true);
    const toRight = nearest(inRowOrder(rightward), tailMember, false);

    const moved = new Set([...toLeft, ...toRight]);
    const rest = people.filter((p) => !moved.has(p));
    if (toLeft.length) rest.splice(rest.indexOf(headMember), 0, ...toLeft);
    if (toRight.length)
      rest.splice(rest.indexOf(tailMember) + 1, 0, ...toRight);
    people = rest;
  }

  return people;
}
